import React, { useEffect } from 'react'
import { useAppDispatch } from '@store/hooks'
import { useNotificationToast } from '@context/NotificationToastContext'
import { internalChatSocket } from '@services/websocket/socket'
import {
  fetchAllInternalChats,
  internalChatParticipantRemoved,
} from '@store/actions/chat-internal.action'
import { createNewTeamMessageToast } from '@utils/notificationToast.util'
import { setActiveSidePanel } from '@store/reducers/uiSlice.reducer'
import { setSelectedChatId } from '@store/reducers/chat-internal.reducer'

const InternalChatSocketListener: React.FC = () => {
  const dispatch = useAppDispatch()
  const { showToast } = useNotificationToast()
  const currentUserId =
    typeof localStorage !== 'undefined'
      ? JSON.parse(localStorage.getItem('user') || '{}').user_id
      : null

  const handleInternalChatMessage = async (payload: {
    conversation_id?: string
    message?: {
      chat_conversation_id?: string
      sender_id?: string
      sender?: {
        first_name?: string
        middle_name?: string
        last_name?: string
      }
      content?: string
      html_content?: string
    }
  }): Promise<void> => {
    if (!payload?.conversation_id || !payload?.message) return
    const { message } = payload
    const isFromOtherUser =
      currentUserId && message.sender_id && message.sender_id !== currentUserId

    const fetchedConversations = await dispatch(
      fetchAllInternalChats(),
    ).unwrap()
    const conversation = (fetchedConversations ?? []).find(
      (c) => c.chat_conversation_id === message.chat_conversation_id,
    )
    const isParticipant = conversation?.chat_participant?.some(
      (p) => p.user_id === currentUserId,
    )

    if (isFromOtherUser && isParticipant) {
      showToast({
        ...createNewTeamMessageToast(() => {
          dispatch(setActiveSidePanel('chat'))
          dispatch(setSelectedChatId(message.chat_conversation_id ?? null))
        }),
      })
    }
  }

  useEffect(() => {
    const handleParticipantRemoved = (payload: {
      chat_participant_id?: string
    }): void => {
      if (!payload?.chat_participant_id) return
      dispatch(
        internalChatParticipantRemoved({
          chat_participant_id: payload.chat_participant_id,
        }),
      )
    }

    const handleConversationsUpdated = (): void => {
      dispatch(fetchAllInternalChats())
    }

    internalChatSocket.on('internal-chat-message', handleInternalChatMessage)
    internalChatSocket.on(
      'internal-chat-participant-removed',
      handleParticipantRemoved,
    )
    internalChatSocket.on(
      'internal-chat-conversations-updated',
      handleConversationsUpdated,
    )
    return () => {
      internalChatSocket.off('internal-chat-message', handleInternalChatMessage)
      internalChatSocket.off(
        'internal-chat-participant-removed',
        handleParticipantRemoved,
      )
      internalChatSocket.off(
        'internal-chat-conversations-updated',
        handleConversationsUpdated,
      )
    }
  }, [dispatch, showToast, currentUserId])

  return null
}

export default InternalChatSocketListener
